/**
 * Championships as data (§5.6, R36): a new one (BFI with Indian states, say) is another entry
 * here, not new game logic. Display names come from i18n by id.
 */
import type { AgeCutoff, CategoryCode } from '../match/category';
import { CATEGORY_CODES, isCategoryCode, isYouthCategory, JANUARY_FIRST } from '../match/category';
import type { ChampionshipId, MatchInfo, MatchInfoError, RoundCode } from '../match/match';
import { matchPlaceProblem, tournamentMatchNumber } from '../match/match';

/** How players must share the game (R34). Quarters are the regular four, not overtime. */
export interface PlayingRule {
  /** Full quarters every listed player must play. */
  minFullQuarters: number;
  /** Most quarters a player may appear in. */
  maxQuarters: number;
  /** A replacement for an injured or fouled-out player comes from those with the fewest quarters. */
  replaceFromFewest: boolean;
}

/** A district or state that enters teams. `name` is the official English spelling. */
export interface ChampionshipUnit {
  id: string;
  name: string;
}

export interface Championship {
  id: ChampionshipId;
  /** Start of its match numbers, before the 2-digit year. */
  codePrefix: string;
  unitKind: 'district' | 'state';
  units: readonly ChampionshipUnit[];
  categories: readonly CategoryCode[];
  ageCutoff: AgeCutoff;
  /** The categories that have a playing rule, and the rule. */
  playingRule: { categories: readonly CategoryCode[]; rule: PlayingRule };
}

/** Government of Tamil Nadu district list, tn.gov.in/district_list.php, checked 2026-11-05. */
const TAMIL_NADU_DISTRICTS: readonly string[] = [
  'Ariyalur',
  'Chengalpattu',
  'Chennai',
  'Coimbatore',
  'Cuddalore',
  'Dharmapuri',
  'Dindigul',
  'Erode',
  'Kallakurichi',
  'Kancheepuram',
  'Kanniyakumari',
  'Karur',
  'Krishnagiri',
  'Madurai',
  'Mayiladuthurai',
  'Nagapattinam',
  'Namakkal',
  'Perambalur',
  'Pudukkottai',
  'Ramanathapuram',
  'Ranipet',
  'Salem',
  'Sivaganga',
  'Tenkasi',
  'Thanjavur',
  'Theni',
  'The Nilgiris',
  'Thoothukudi',
  'Tiruchirappalli',
  'Tirunelveli',
  'Tirupathur',
  'Tiruppur',
  'Tiruvallur',
  'Tiruvannamalai',
  'Tiruvarur',
  'Vellore',
  'Viluppuram',
  'Virudhunagar',
];

const unitId = (name: string) => name.toLowerCase().replace(/[^a-z]+/g, '-');

export const TNBA: Championship = {
  id: 'tnba',
  codePrefix: 'TNBA',
  unitKind: 'district',
  units: TAMIL_NADU_DISTRICTS.map((name) => ({ id: unitId(name), name })),
  categories: CATEGORY_CODES,
  ageCutoff: JANUARY_FIRST,
  playingRule: {
    categories: CATEGORY_CODES.filter(isYouthCategory),
    rule: { minFullQuarters: 1, maxQuarters: 3, replaceFromFewest: true },
  },
};

const CHAMPIONSHIPS: Readonly<Record<ChampionshipId, Championship>> = { tnba: TNBA };

export function getChampionship(id: ChampionshipId): Championship {
  return CHAMPIONSHIPS[id];
}

/** The playing rule for a category, or null when it has none. */
export function playingRuleFor(
  championship: Championship,
  category: CategoryCode,
): PlayingRule | null {
  const { categories, rule } = championship.playingRule;
  return categories.includes(category) ? rule : null;
}

/** `TNBA` and 2026 → `TNBA26`. */
export function championshipCode(championship: Championship, year: number): string {
  return `${championship.codePrefix}${String(year % 100).padStart(2, '0')}`;
}

const MIN_YEAR = 2000;
const MAX_YEAR = 2099;

export function championshipYearOk(year: number): boolean {
  return Number.isInteger(year) && year >= MIN_YEAR && year <= MAX_YEAR;
}

export interface ChampionshipMatchInput {
  championship: Championship;
  year: number;
  category: CategoryCode;
  round: RoundCode;
  /** Match number within this year and category. */
  matchNo: number;
  court?: string;
}

/** Builds a championship game's MatchInfo (`TNBA26-U16B-QF-14`), or the first problem. */
export function createChampionshipMatch(
  input: ChampionshipMatchInput,
): { ok: true; match: MatchInfo } | { ok: false; error: MatchInfoError } {
  const { championship, year, category, round, matchNo } = input;
  if (!championshipYearOk(year)) return { ok: false, error: 'INVALID_YEAR' };
  if (!isCategoryCode(category) || !championship.categories.includes(category)) {
    return { ok: false, error: 'INVALID_CATEGORY' };
  }
  const problem = matchPlaceProblem(round, matchNo, input.court);
  if (problem !== null) return { ok: false, error: problem };
  const court = input.court?.trim();
  const code = `${championshipCode(championship, year)}-${category}`;
  return {
    ok: true,
    match: {
      number: tournamentMatchNumber(code, round, matchNo),
      type: 'championship',
      championship: { id: championship.id, year },
      category,
      matchNo,
      round,
      ...(court ? { court } : {}),
    },
  };
}

/**
 * The name for a unit's next team in one category and year (R35), given its other teams' names.
 * The first team is `Chennai`. The second makes them `Chennai A` and `Chennai B`, and later teams
 * take the first free letter.
 */
export function nextUnitTeamName(
  unitName: string,
  existing: readonly string[],
): { name: string; rename?: { from: string; to: string } } {
  const plain = existing.find((name) => nameKey(name) === nameKey(unitName));
  if (plain === undefined && !existing.some((name) => isLetteredName(unitName, name))) {
    return { name: unitName };
  }
  const taken = new Set(existing.map(nameKey));
  let i = 0;
  const nextFree = (): string => {
    while (taken.has(nameKey(`${unitName} ${teamLetter(i)}`))) i += 1;
    const name = `${unitName} ${teamLetter(i)}`;
    taken.add(nameKey(name));
    return name;
  };
  if (plain === undefined) return { name: nextFree() };
  const to = nextFree();
  return { name: nextFree(), rename: { from: plain, to } };
}

const nameKey = (name: string): string => name.trim().toLocaleLowerCase();

/** `Chennai`, `Chennai A`, `Chennai AB`: a name that came from the unit, not one of its own. */
export function isUnitTeamName(unitName: string, name: string): boolean {
  return nameKey(name) === nameKey(unitName) || isLetteredName(unitName, name);
}

function isLetteredName(unitName: string, name: string): boolean {
  const prefix = `${nameKey(unitName)} `;
  const key = nameKey(name);
  return key.startsWith(prefix) && /^[a-z]{1,2}$/.test(key.slice(prefix.length));
}

/** A, B … Z, then AA, AB and so on. A district will not get that far, but a name is never wrong. */
function teamLetter(index: number): string {
  const letter = String.fromCharCode(65 + (index % 26));
  return index < 26 ? letter : `${teamLetter(Math.floor(index / 26) - 1)}${letter}`;
}
